/**
 * <s-launch-pass id="launch-pass">: three boarding passes (Essential, Premium, Custom).
 * - "Fine print" buttons send command="--flip" with data-pass-for: that pass gets [data-flipped] (CSS turns the
 *   card), the hidden face becomes inert and focus moves to the other face's button.
 * - Fine pointers with motion allowed: the pass under the pointer tilts slightly (--rx/--ry) and a light sweep
 *   follows (--sx). Listeners sit on the untransformed <li>.
 * - Opening the quiz also opens the table behind "Compare every detail", so its result ring has a table.
 */
class SLaunchPass extends HTMLElement {
  #fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  #still = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Help bar: --quiz / --compare open (or close) their panel below and scroll it into view.
  #toggle(details, button) {
    if (!details) return;
    details.open = !details.open;
    if (details.open) {
      const smooth = !this.#still.matches;
      requestAnimationFrame(() => details.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' }));
    }
    this.#sync();
  }

  #sync = () => {
    const demo = this.closest('s-launch-demo');
    const quiz = demo?.querySelector('.tier-quiz-more');
    const compare = demo?.querySelector('[data-compare-details]');
    this.querySelector('[command="--quiz"]')?.setAttribute('aria-expanded', String(!!quiz?.open));
    this.querySelector('[command="--compare"]')?.setAttribute('aria-expanded', String(!!compare?.open));
  };

  #onCommand = (event) => {
    if (event.command === '--quiz' || event.command === '--compare') {
      const demo = this.closest('s-launch-demo');
      const target = event.command === '--quiz' ? demo?.querySelector('.tier-quiz-more') : demo?.querySelector('[data-compare-details]');
      this.#toggle(target, event.source);
      return;
    }
    if (event.command !== '--flip') return;
    const item = this.querySelector(`.launch-pass-item[data-pass="${event.source?.dataset.passFor}"]`);
    if (!item) return;
    const flipped = !item.hasAttribute('data-flipped');
    item.toggleAttribute('data-flipped', flipped);
    const front = item.querySelector('.launch-pass-front');
    const back = item.querySelector('.launch-pass-back');
    if (front) front.inert = flipped;
    if (back) back.inert = !flipped;
    item.querySelector('.launch-pass-front .launch-pass-flip')?.setAttribute('aria-expanded', String(flipped));
    (flipped ? back : front)?.querySelector('.launch-pass-flip')?.focus({ preventScroll: true });
  };

  #onMove = (event) => {
    if (!this.#fine.matches || this.#still.matches) return;
    const item = event.target.closest('.launch-pass-item');
    if (!item) return;
    const box = item.getBoundingClientRect();
    const px = (event.clientX - box.left) / box.width;
    const py = (event.clientY - box.top) / box.height;
    item.style.setProperty('--ry', `${((px - 0.5) * 8).toFixed(2)}deg`);
    item.style.setProperty('--rx', `${((0.5 - py) * 6).toFixed(2)}deg`);
    item.style.setProperty('--sx', `${(px * 100).toFixed(1)}%`);
    item.setAttribute('data-hover', '');
  };

  #onOut = (event) => {
    const item = event.target.closest('.launch-pass-item');
    if (!item || item.contains(event.relatedTarget)) return;
    item.removeAttribute('data-hover');
    item.style.setProperty('--rx', '0deg');
    item.style.setProperty('--ry', '0deg');
  };

  #onQuiz = (event) => {
    if (!event.target.open) { this.#sync(); return; }
    const compare = this.closest('s-launch-demo')?.querySelector('[data-compare-details]');
    if (compare && !compare.open) compare.open = true;
    this.#sync();
  };

  connectedCallback() {
    this.addEventListener('command', this.#onCommand);
    this.addEventListener('pointermove', this.#onMove);
    this.addEventListener('pointerout', this.#onOut);
    this.quiz = this.closest('s-launch-demo')?.querySelector('.tier-quiz-more');
    this.quiz?.addEventListener('toggle', this.#onQuiz);
    this.compare = this.closest('s-launch-demo')?.querySelector('[data-compare-details]');
    this.compare?.addEventListener('toggle', this.#sync);
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.#onCommand);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerout', this.#onOut);
    this.quiz?.removeEventListener('toggle', this.#onQuiz);
    this.compare?.removeEventListener('toggle', this.#sync);
  }
}

if (!customElements.get('s-launch-pass')) customElements.define('s-launch-pass', SLaunchPass);
